import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { borderRadius, tabHeight, } from './constants';
import { TabToggleButton } from './TabToggleButton';
import { TabToggleButtonContext } from './TabToggleButtonContext';
import { TabToggleSwitchContext } from './TabToggleSwitchContext';
const borderWidth = 1;
const gapWidth = styleUtils.remToPxValue(theme.spacing.base);
const containerStyles = css `
  ${styleUtils.borderBox}
  border-color: ${theme.colors.gray400};
  border-radius: ${styleUtils.rem(borderRadius)};
  border-style: solid;
  border-width: ${styleUtils.rem(borderWidth)};
  display: flex;
  padding-bottom: ${theme.spacing.x2};
  padding-left: ${theme.spacing.x2};
  padding-right: ${theme.spacing.x2};
  padding-top: ${theme.spacing.x2};
`;
const defaultProps = {
    initialSelectedTab: 1,
};
const TabToggleSwitch = ({ initialSelectedTab = defaultProps.initialSelectedTab, tabs, }) => {
    const [movingTabWidth, setmovingTabWidth,] = useState(0);
    const [movingTabOffset, setmovingTabOffset,] = useState(0);
    const [currentTabNumber, setCurrentTabNumber,] = useState(initialSelectedTab);
    const containerRef = useRef(null);
    const movingTabRef = useRef(null);
    const tabCount = useMemo(() => (tabs.length), [
        tabs,
    ]);
    const tabGroupStyles = useMemo(() => (css `
        align-items: center;
        border-radius: ${styleUtils.rem(borderRadius)};
        box-sizing: border-box;
        column-gap: ${styleUtils.rem(gapWidth)};
        display: grid;
        grid-template-columns: repeat(${tabCount}, 1fr);
        height: ${styleUtils.rem(tabHeight)};
        justify-content: space-around;
        position: relative;
        width: 100%;
      `), [
        tabCount,
    ]);
    const movingTabStyles = useMemo(() => (css `
        background-color: ${theme.colors.gray800};
        border-radius: ${styleUtils.rem(borderRadius)};
        height: ${styleUtils.rem(tabHeight)};
        left: ${styleUtils.rem(movingTabOffset)};
        position: absolute;
        transition: left 250ms ease-in-out;
        width: ${styleUtils.rem(movingTabWidth)};
      `), [
        movingTabOffset,
        movingTabWidth,
    ]);
    const providerValue = useMemo(() => ({
        currentTabNumber,
    }), [
        currentTabNumber,
    ]);
    const onUpdate = useCallback(() => {
        var _a;
        const totalGapWidth = gapWidth * (tabCount - 1);
        const tabWidth = (((((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth)
            || 0)
            - totalGapWidth)
            / tabCount);
        setmovingTabWidth(tabWidth);
    }, [
        tabCount,
    ]);
    const onSelectTab = useCallback((tabNumber) => {
        setmovingTabOffset((movingTabWidth + gapWidth) * (tabNumber - 1));
        setCurrentTabNumber(tabNumber);
    }, [
        movingTabWidth,
    ]);
    const tabData = useMemo(() => (tabs
        .map((tab, index) => {
        const label = `${index + 1}-${tab.label.replace(' ', '_')}`;
        return (Object.assign(Object.assign({}, tab), { panelId: ('panel-'
                .concat(label)), tabId: ('tab-'
                .concat(label)), tabNumber: index + 1 }));
    })), [
        tabs,
    ]);
    const tabButtons = useMemo(() => (tabData
        .map(({ label, onClick, panelId, tabId, tabNumber, }) => (_jsx(TabToggleButtonContext.Provider, Object.assign({ value: {
            panelId,
            tabId,
            tabNumber,
        } }, { children: _jsx(TabToggleButton, { onClick: (event) => {
                onSelectTab(tabNumber);
                onClick(event);
            }, text: label }) }), tabNumber)))), [
        tabData,
        onSelectTab,
    ]);
    const tabPanels = useMemo(() => (tabData
        .map(({ content, panelId, tabNumber, }) => (_jsx("div", Object.assign({ hidden: currentTabNumber !== tabNumber, id: panelId, role: "tabpanel" }, { children: content }), tabNumber)))), [
        currentTabNumber,
        tabData,
    ]);
    useEffect(() => {
        onUpdate();
    }, [
        onUpdate,
    ]);
    useEffect(() => {
        window.addEventListener('resize', onUpdate);
        return () => {
            window.removeEventListener('resize', onUpdate);
        };
    }, [
        onUpdate,
    ]);
    return (_jsxs(TabToggleSwitchContext.Provider, Object.assign({ value: providerValue }, { children: [_jsx("div", Object.assign({ css: containerStyles }, { children: _jsxs("div", Object.assign({ css: tabGroupStyles, ref: containerRef, role: "tablist" }, { children: [_jsx("div", { css: movingTabStyles, ref: movingTabRef }), tabButtons] })) })), tabPanels] })));
};
const MemoizedTabToggleSwitch = memo(TabToggleSwitch);
export { MemoizedTabToggleSwitch as TabToggleSwitch };
// MC-1068 Temporary hack for Storybook.
export default TabToggleSwitch;
